/**
 * ArrivalsBoard (#564) — the printable "morning board".
 *
 * From the 2026-07-21 owner walkthrough: every morning HTHD staff print
 * Gingr's expected-dogs list and hand-copy it onto a whiteboard. This is
 * that paper board — a print-friendly, dog-first roster of who's coming in
 * and who's going home today, grouped by service, with the flags a front-
 * desk worker needs at a glance. Ted #28 Phase 2 (#1050): the flags are the
 * ONE registry's (vax / INTACT / meds / transport beside each dog, the
 * owner's `$` / credit / agreement under the owner) drawn by the ONE
 * renderer — the board used to grade its own worst-vax / meds / balance
 * chips, and `flag-surface-coverage.test.ts` refuses their return.
 *
 * Print mechanics mirror components/run-cards/RunCardPrintLayout:
 *  - The board renders through a PORTAL to document.body so it is a direct
 *    child of <body>. The print stylesheet hides `body > *:not([data-print
 *    -root])`, so only this board prints — no app shell, nav, or toolbar.
 *  - Screen view keeps a sticky toolbar (Print / Close); print view drops it.
 *
 * Data comes straight from the page's already-fetched `/today` payload
 * (Principle 1 — one source; the board can never disagree with the arrivals
 * list it was printed from). No new fetch.
 *
 * Design-Philosophy principles served:
 *  - #1 One glance, one truth — dog name is the hero, grouped so a person
 *    can find a service block instantly; the board is the SAME `/today`
 *    data the dashboard/front-desk show.
 *  - #3 Deference — the dog's name is bigger than our labels; the sheet is
 *    flat black on white (a deliberate, documented print exception to the
 *    token rule — a morning board must read on any office laser printer and
 *    photocopy clean onto a whiteboard).
 *  - #5 Never lie about data — when the server flags today's zeros as
 *    structural (stale mirror / nothing loaded), the board says so, on
 *    screen AND on the printout; a stale board must never masquerade as a
 *    complete one.
 *  - #7 Built for hands — the launch button and toolbar targets are ≥44px.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  formatTime,
  type DashboardArrival,
  type DashboardDeparture,
  type DogCard,
  // Sawyer 03 today-notes-board (A4)
  deskNotesOf,
} from '../dashboard/dashboard-api.js';
// Sawyer 03 today-notes-board (A4) — the LUNCH tag (seam for lane 08's marker).
import { lunchTodayFor } from '../dashboard/lunch-marker.js';
import {
  foldServiceLegs,
  serviceRowLabel,
  type VisitWithLegs,
} from '../dashboard/service-legs.js';
import { DogFlagChips } from '../dog-first/index.js';
import { resolveDogFlags } from '../dog-first/flag-skew.js';
import './arrivals-board.css';

interface ArrivalsBoardProps {
  readonly facilityName: string;
  /** ISO date (YYYY-MM-DD) the board is for. */
  readonly date: string;
  readonly arrivals: readonly DashboardArrival[];
  readonly departures: readonly DashboardDeparture[];
  /**
   * Sawyer 03 today-notes-board (A4) — `/today.onSite`: checked-in boarders
   * mid-stay (neither arriving nor leaving today). undefined = an older
   * server: the section is not rendered at all (never a false "none").
   */
  readonly inHouse?: readonly DashboardArrival[];
  /** #5 — server says today's zeros may be structural (stale/absent data). */
  readonly dataSuspect: boolean;
  /** The server's verbatim data-status warning, if any (screen only). */
  readonly warning: string | null;
  readonly onClose: () => void;
  /** Open the OS print dialog as soon as the board mounts. */
  readonly autoPrint?: boolean;
}

/** Canonical service order, then any other service names alphabetically. */
const SERVICE_ORDER = ['daycare', 'boarding', 'grooming'];

function serviceRank(name: string): number {
  const i = SERVICE_ORDER.indexOf(name.toLowerCase());
  return i === -1 ? SERVICE_ORDER.length : i;
}

function serviceLabel(name: string): string {
  const known: Record<string, string> = {
    daycare: 'Daycare',
    boarding: 'Boarding',
    grooming: 'Grooming',
  };
  return known[name.toLowerCase()] ?? name;
}

type BoardEntry = VisitWithLegs<DashboardArrival>;

interface ServiceGroup {
  service: string;
  items: readonly BoardEntry[];
}

/**
 * Group a day's VISITS by service, in canonical service order.
 *
 * Ted #23 (#984), 09-16 re-review: the rows arrive from `/today`, and a
 * service leg is a Booking, so this used to bucket Milo's nail trim and bath
 * as two extra rows under "Grooming" — one dog printed as three arrivals with
 * two identical names. `foldServiceLegs` folds each leg onto the visit it
 * hangs off first; the visit is grouped by ITS service and prints its added
 * services on the row (see BoardRow). A leg whose parent is not on this feed
 * still gets its own row, grouped by the service it rides on and labelled
 * with the add-on's name — never dropped, never untagged.
 */
function groupByService(items: readonly DashboardArrival[]): ServiceGroup[] {
  const byService = new Map<string, BoardEntry[]>();
  for (const entry of foldServiceLegs(items)) {
    const key = entry.row.serviceType;
    const bucket = byService.get(key);
    if (bucket) bucket.push(entry);
    else byService.set(key, [entry]);
  }
  return [...byService.entries()]
    .map(([service, groupItems]) => ({ service, items: groupItems }))
    .sort(
      (a, b) =>
        serviceRank(a.service) - serviceRank(b.service) ||
        a.service.localeCompare(b.service),
    );
}

/** How many entries the board says to expect: VISITS, not booking rows (#1029). */
function countEntries(groups: readonly ServiceGroup[]): number {
  return groups.reduce((sum, g) => sum + g.items.length, 0);
}

function initialOf(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : '?';
}

function BoardRow({ entry }: { entry: BoardEntry }): JSX.Element {
  const item = entry.row;
  const time = formatTime(item.expectedTime);
  // Ted #28 Phase 2 — per-DOG flags (which dog has the lapsed shot, which is
  // intact, which has meds), and the owner's under the owner. The legacy
  // row facts feed the deploy-window shim only (`flag-skew.ts`).
  const dogs: readonly DogCard[] = Array.isArray(item.dogs) ? item.dogs : [];
  const legacy = {
    unpaid: item.unpaid,
    balanceDueCents: item.balanceDueCents,
    storeCreditCents: item.walletBalanceCents,
    hasMedications: item.hasMedications,
  };
  const lead = dogs[0];
  // Ted #23 (#984), 09-16 re-review — the added services print ON the visit,
  // so the board says "Milo … + Nail Trim, Bath" instead of printing Milo
  // three times. An orphan leg (rule 1 of the fold) names itself instead.
  const addedServices = entry.legs.map(serviceRowLabel).filter((l) => l.length > 0);
  // Sawyer 03 today-notes-board (A4) — the visit's notes in full under the
  // dog (the paper copy carries the instruction), and the LUNCH tag. An
  // orphan leg prints neither: notes and lunch belong to the visit.
  const bookingNote = entry.orphanLeg ? null : (item.bookingNote ?? null);
  const deskNotes = entry.orphanLeg ? [] : (deskNotesOf(item) ?? []);
  const lunch = lunchTodayFor(entry);
  return (
    <li className="ferro-board__row" data-testid={`board-row-${item.bookingId}`}>
      <span className="ferro-board__avatar" aria-hidden="true">
        {initialOf(item.dogNames[0] ?? '?')}
      </span>
      <span className="ferro-board__ident">
        <span className="ferro-board__dog">
          {dogs.length > 0
            ? dogs.map((d, i) => (
                <span key={d.dogId} className="ferro-board__dog-entry">
                  {i > 0 ? ', ' : ''}
                  {d.name}
                  <span className="ferro-board__dog-flags" data-testid={`board-dog-flags-${d.dogId}`}>
                    <DogFlagChips
                      flags={resolveDogFlags(d, i === 0 ? legacy : undefined)}
                      scope="dog"
                      testIdPrefix={`board-${d.dogId}-`}
                    />
                  </span>
                </span>
              ))
            : item.dogNames.join(', ')}
          {lunch ? (
            <span className="ferro-board__lunch" data-testid={`board-lunch-${item.bookingId}`}>
              LUNCH
            </span>
          ) : null}
        </span>
        <span className="ferro-board__owner">
          {item.customerName}
          {lead ? (
            <span className="ferro-board__owner-flags" data-testid={`board-owner-flags-${item.bookingId}`}>
              <DogFlagChips flags={resolveDogFlags(lead, legacy)} scope="account" testIdPrefix={`board-${item.bookingId}-`} />
            </span>
          ) : null}
        </span>
        {entry.orphanLeg ? (
          <span
            className="ferro-board__added"
            data-testid={`board-leg-${item.bookingId}`}
          >
            Added service: {serviceRowLabel(item)}
          </span>
        ) : null}
        {addedServices.length > 0 ? (
          <span
            className="ferro-board__added"
            data-testid={`board-added-${item.bookingId}`}
          >
            + {addedServices.join(', ')}
          </span>
        ) : null}
        {bookingNote !== null && bookingNote.trim().length > 0 ? (
          <span className="ferro-board__note" data-testid={`board-note-${item.bookingId}`}>
            <strong>Note:</strong> {bookingNote}
          </span>
        ) : null}
        {deskNotes.map((d) => (
          <span
            key={d.dogId}
            className="ferro-board__note"
            data-testid={`board-desk-note-${item.bookingId}-${d.dogId}`}
          >
            <strong>Desk note ({d.dogName}):</strong> {d.note}
          </span>
        ))}
      </span>
      <span className="ferro-board__time">{time || '—'}</span>
    </li>
  );
}

function BoardSection({
  title,
  count,
  groups,
  emptyCopy,
  testid,
}: {
  title: string;
  count: number;
  groups: ServiceGroup[];
  emptyCopy: string;
  testid: string;
}): JSX.Element {
  return (
    <section className="ferro-board__section" data-testid={testid}>
      <h2 className="ferro-board__section-title">
        {title} <span className="ferro-board__section-count">({count})</span>
      </h2>
      {groups.length === 0 ? (
        <p className="ferro-board__empty">{emptyCopy}</p>
      ) : (
        groups.map((group) => (
          <div key={group.service} className="ferro-board__group">
            <h3 className="ferro-board__group-title">
              {serviceLabel(group.service)}{' '}
              <span className="ferro-board__group-count">
                ({group.items.length})
              </span>
            </h3>
            <ul className="ferro-board__list">
              {group.items.map((entry) => (
                <BoardRow key={entry.row.bookingId} entry={entry} />
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}

/**
 * Sawyer 03 today-notes-board (A4) — "Lunches today": every lunch-marked dog
 * across the day's sections, once each. The empty copy names what ALA knows
 * ("marked"), never "nobody eats lunch" (Principle 5).
 */
function LunchesSection({ entries }: { entries: readonly BoardEntry[] }): JSX.Element {
  const seen = new Set<string>();
  const lunches = entries.filter((e) => {
    if (seen.has(e.row.bookingId) || !lunchTodayFor(e)) return false;
    seen.add(e.row.bookingId);
    return true;
  });
  return (
    <section className="ferro-board__section" data-testid="board-section-lunches">
      <h2 className="ferro-board__section-title">
        Lunches today <span className="ferro-board__section-count">({lunches.length})</span>
      </h2>
      {lunches.length === 0 ? (
        <p className="ferro-board__empty">No lunches marked for today.</p>
      ) : (
        <ul className="ferro-board__list">
          {lunches.map((e) => (
            <li
              key={e.row.bookingId}
              className="ferro-board__row ferro-board__row--lunch"
              data-testid={`board-lunch-item-${e.row.bookingId}`}
            >
              <span className="ferro-board__ident">
                <span className="ferro-board__dog">{e.row.dogNames.join(', ')}</span>
                <span className="ferro-board__owner">{e.row.customerName}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ArrivalsBoard({
  facilityName,
  date,
  arrivals,
  departures,
  inHouse,
  dataSuspect,
  warning,
  onClose,
  autoPrint = false,
}: ArrivalsBoardProps): JSX.Element {
  useEffect(() => {
    if (!autoPrint) return;
    const id = window.requestAnimationFrame(() => window.print());
    return () => window.cancelAnimationFrame(id);
  }, [autoPrint]);

  // Esc closes the board (keyboard parity with the shared Modal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const arrivalGroups = groupByService(arrivals);
  const departureGroups = groupByService(departures);
  // Ted #23 (#984), 09-16 re-review — the headline counts VISITS EXPECTED, not
  // booking rows. `arrivals.length` counted each added service as a separate
  // arrival, so Milo with a nail trim and a bath printed "Expected today (3)".
  const arrivalCount = countEntries(arrivalGroups);
  const departureCount = countEntries(departureGroups);
  // Sawyer 03 today-notes-board (A4) — mid-stay boarders; absent ⇒ no section.
  const inHouseGroups = inHouse ? groupByService(inHouse) : [];
  const inHouseCount = countEntries(inHouseGroups);
  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const printedAt = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const totalToday = arrivalCount + departureCount + inHouseCount;

  const board = (
    <div
      className="ferro-board-root"
      data-print-root="true"
      data-testid="arrivals-board"
      role="region"
      aria-label={`Morning board for ${dateLabel}`}
    >
      {/* Screen-only toolbar — hidden by @media print. */}
      <div className="ferro-board-toolbar">
        <strong>Morning board · {dateLabel}</strong>
        <div className="ferro-board-toolbar__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
            data-testid="arrivals-board-close"
          >
            Close
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => window.print()}
            data-testid="arrivals-board-print"
          >
            Print
          </button>
        </div>
      </div>

      <div className="ferro-board-doc">
        <header className="ferro-board__header">
          <div>
            <p className="ferro-board__facility">{facilityName}</p>
            <h1 className="ferro-board__title">Morning Board</h1>
            <p className="ferro-board__date">{dateLabel}</p>
          </div>
          <p className="ferro-board__printed">Printed {printedAt}</p>
        </header>

        {/* #5 — a stale/absent-data board must SAY so, on paper too. */}
        {dataSuspect ? (
          <p className="ferro-board__notice" data-testid="arrivals-board-notice">
            Heads up: today’s schedule may be incomplete in this system — check
            the previous system for the full day before relying on this board.
          </p>
        ) : null}
        {/* Screen-only: the server's verbatim warning copy. */}
        {warning ? (
          <p className="ferro-board__warning" role="status">
            {warning}
          </p>
        ) : null}

        {totalToday === 0 ? (
          <p className="ferro-board__empty-all" data-testid="arrivals-board-empty">
            {dataSuspect
              ? 'No dogs loaded for today in this system yet.'
              : 'No dogs expected today.'}
          </p>
        ) : (
          <>
            <BoardSection
              title="Expected today"
              count={arrivalCount}
              groups={arrivalGroups}
              emptyCopy={
                dataSuspect
                  ? 'No arrivals loaded for today.'
                  : 'No arrivals expected today.'
              }
              testid="board-section-arrivals"
            />
            {/* Sawyer 03 today-notes-board (A4; refuter C-7) — `onSite` holds
                only mid-stay boarders; checked-in daycare is "Going home". */}
            {inHouse ? (
              <BoardSection
                title="Staying over (mid-stay)"
                count={inHouseCount}
                groups={inHouseGroups}
                emptyCopy="No mid-stay boarders today."
                testid="board-section-in-house"
              />
            ) : null}
            <BoardSection
              title="Going home today"
              count={departureCount}
              groups={departureGroups}
              emptyCopy={
                dataSuspect
                  ? 'No departures loaded for today.'
                  : 'No dogs going home today.'
              }
              testid="board-section-departures"
            />
            <LunchesSection
              entries={[...arrivalGroups, ...inHouseGroups, ...departureGroups].flatMap((g) => g.items)}
            />
          </>
        )}
      </div>
    </div>
  );

  return createPortal(board, document.body);
}
